"use client"

import { ArrowsOut, Export, Gear, Tag, User } from "@phosphor-icons/react"
import { ChartWithConfigId } from "@tokenterminal/core/feature/chart/ChartWithConfigId"
import { ChartContextProvider } from "@tokenterminal/core/feature/chart/tt-ui-chart/ChartContext"
import { ClientOnlySuspense } from "@/components/ClientOnlySuspense"
import { FADE_MASK_STYLE } from "@/utils/masonryMask"

// Chart config IDs for the dashboard
const CHART_CONFIG_ID_1 = "MzM1ZTlkMDMyZTA1MzA4ZmU0MzMyZWVl" // ETH economy chart
const CHART_CONFIG_ID_2 = "MjhhNDI1OGE3ZjkzNmYzYmMwZmMzNjcz" // ETH vs TVL chart

// Ethereum logo component
function EthereumLogo({ className }: { className?: string }) {
  return (
    <div
      className={`w-12 h-12 rounded-lg bg-[#627EEA] flex items-center justify-center ${className}`}
    >
      <svg viewBox="0 0 32 32" className="w-7 h-7" fill="white">
        <path d="M16 4L8 16.5L16 21L24 16.5L16 4Z" fillOpacity="0.8" />
        <path d="M8 18L16 28L24 18L16 22.5L8 18Z" />
      </svg>
    </div>
  )
}

// Card action icons
function CardActions() {
  return (
    <div className="flex items-center gap-1.5">
      <button className="p-0.5 text-fg-secondary hover:text-fg-default transition-colors">
        <Gear className="w-3 h-3" />
      </button>
      <button className="p-0.5 text-fg-secondary hover:text-fg-default transition-colors">
        <Export className="w-3 h-3" />
      </button>
      <button className="p-0.5 text-fg-secondary hover:text-fg-default transition-colors">
        <ArrowsOut className="w-3 h-3" />
      </button>
    </div>
  )
}

// Number metric card
function MetricCard({
  title,
  subtitle,
  value,
}: {
  title: string
  subtitle: string
  value: string
}) {
  return (
    <div className="bg-bg-default border border-border-default rounded-lg p-5 flex flex-col h-full">
      <div className="flex items-start justify-between mb-1">
        <div>
          <div className="text-sm font-medium text-fg-default">{title}</div>
          <div className="text-xs text-fg-secondary">{subtitle}</div>
        </div>
        <CardActions />
      </div>
      <div className="flex-1 flex items-center justify-center pt-4">
        <div className="text-4xl md:text-5xl font-light text-fg-default tracking-tight">
          {value}
        </div>
      </div>
    </div>
  )
}

// Chart card wrapper
function ChartCard({ title, chartId }: { title: string; chartId: string }) {
  return (
    <div className="bg-bg-default border border-border-default rounded-lg p-5 flex flex-col h-full">
      <div className="flex items-start justify-between mb-4">
        <div className="text-sm font-medium text-fg-default">{title}</div>
        <CardActions />
      </div>
      <div className="flex-1 min-h-0">
        <ClientOnlySuspense>
          <ChartContextProvider>
            <ChartWithConfigId
              id={chartId}
              isVisible
              showDescription={false}
              showTitle={false}
              showLegend
            />
          </ChartContextProvider>
        </ClientOnlySuspense>
      </div>
    </div>
  )
}

function BuildComposableDashboards() {
  return (
    <div
      className="relative border-t border-l border-solid border-border-default rounded-tl-xl bg-bg-inset"
      style={{ overflow: "hidden", ...FADE_MASK_STYLE }}
    >
      <div className="p-6" style={{ width: "calc(100% + 300px)" }}>
        {/* Dashboard Header */}
        <div className="flex items-start gap-4 mb-8">
          <EthereumLogo />
          <div className="flex-1">
            <h3 className="text-lg font-medium text-fg-default mb-1">The Ethereum (ETH) Economy</h3>
            <p className="text-sm text-fg-secondary mb-3">
              How does the valuation of ETH correlate with the size of the Ethereum economy?
            </p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-xs text-fg-secondary">
                <User className="w-3.5 h-3.5" />
                <span>Token Terminal</span>
                <span className="w-2 h-2 rounded-full bg-green-500" />
              </div>
              <div className="flex items-center gap-2">
                <Tag className="w-3.5 h-3.5 text-fg-secondary" />
                <span className="text-xs px-2 py-0.5 rounded bg-bg-subtle text-fg-secondary border border-border-default">
                  Blockchains (L1)
                </span>
                <span className="text-xs px-2 py-0.5 rounded bg-bg-subtle text-fg-secondary border border-border-default">
                  Ethereum
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Analysis Section */}
        <div className="mb-8">
          <h4 className="text-base font-medium text-fg-default mb-4">
            How does the valuation of ETH correlate with the size of the Ethereum economy?
          </h4>
          <ul className="space-y-2 text-sm text-fg-secondary list-disc list-inside">
            <li>
              For more than five years, total value locked (TVL) on Ethereum has established a floor
              for ETH&apos;s valuation.
            </li>
            <li>
              If this pattern were to continue, it&apos;s worth studying how different DeFi,
              stablecoin, and RWA applications
            </li>
            <li>
              Under this model, if Ethereum-based applications increase their combined TVL to $1
              trillion → $1 trillion
            </li>
          </ul>
        </div>

        {/* Metric Cards Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <MetricCard title="ETH" subtitle="Fully diluted market cap" value="$400.7 B" />
          <MetricCard title="Ethereum" subtitle="Total value locked (TVL)" value="$345.1 B" />
          <MetricCard
            title="ETH valuation multiple"
            subtitle="ETH fully diluted market cap / Ethereum TVL"
            value="1.2X"
          />
        </div>

        {/* Chart Cards Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="h-[480px]">
            <ChartCard
              title="The value of ETH vs. the size of the Ethereum economy"
              chartId={CHART_CONFIG_ID_1}
            />
          </div>
          <div className="h-[480px]">
            <ChartCard
              title="The value of ETH vs. the size of the Ethereum economy"
              chartId={CHART_CONFIG_ID_2}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default BuildComposableDashboards
